import classNames from 'classnames';
import Image from '@/shared/components/shared/Image';
import Link from '@/shared/components/shared/Link';
import { useTypograf } from '@/shared/hooks/use-typograf';
import { ImageShape } from '@/shared/lib/types';
import Button from '../../shared/Button';
import Typografed from '../../shared/Typografed';

export type YearParticipantsSectionItem = {
    logo?: ImageShape | null;
    href?: string | null;
    title?: string | null;
};

export type YearParticipantsSectionRawProps = {
    title?: string | null;
    text?: string | null;
    items?: YearParticipantsSectionItem[] | null;
    button?: {
        text: string;
        href: string;
    } | null;
};

type Props = React.HTMLAttributes<HTMLElement> & {
    yearParticipantsSectionProps: YearParticipantsSectionRawProps;
};

function YearParticipantsSectionCard({ logo, href, title }: YearParticipantsSectionItem) {
    if (!logo?.src) {
        return null;
    }

    const image = (
        <Image
            className="year-participants-section__img"
            src={logo.src}
            alt={logo.alt || title || ''}
            title={logo.title || title || undefined}
            width={logo.width}
            height={logo.height}
            sizes="210px"
            unoptimized={logo.src.endsWith('.svg')}
        />
    );

    if (href) {
        return (
            <Link href={href} className="year-participants-section__card" aria-label={title || 'Участник'}>
                {image}
            </Link>
        );
    }

    return <div className="year-participants-section__card">{image}</div>;
}

function YearParticipantsSection({ yearParticipantsSectionProps, className, ...props }: Props) {
    const { title, text, items, button } = yearParticipantsSectionProps;
    const tpTitle = useTypograf(title ?? '');
    const tpText = useTypograf(text ?? '');
    const filteredItems = items?.filter((item) => item.logo?.src) ?? [];

    if (!tpTitle && !tpText && filteredItems.length === 0) {
        return null;
    }

    return (
        <section
            {...props}
            className={classNames('year-participants-section js-header-theme-trigger', className)}
            data-theme="dark"
        >
            <div className="wrapper year-participants-section__wrapper">
                {(tpTitle || tpText || button) && (
                    <div className="year-participants-section__info">
                        {tpTitle && <h2 className="h3 year-participants-section__title">{tpTitle}</h2>}
                        {tpText && <div className="text-l year-participants-section__text">{tpText}</div>}
                        {button && (
                            <Button
                                tag={Link}
                                href={button.href}
                                className="organize-left-btn"
                                variant="outline-primary"
                            >
                                <Typografed>{button.text}</Typografed>
                            </Button>
                        )}
                    </div>
                )}

                {filteredItems.length > 0 && (
                    <ul className="list-unstyled year-participants-section__list">
                        {filteredItems.map((item, index) => (
                            <li
                                key={item.logo?.src ? `${item.logo.src}-${index}` : index}
                                className="year-participants-section__item"
                            >
                                <YearParticipantsSectionCard {...item} />
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </section>
    );
}

export default YearParticipantsSection;
